import { ConsulationFlowType } from '@/constants';
import {RootState} from '../rootReducer';
import {createSelector} from '@reduxjs/toolkit';

export const patientDashboardLoadingStatus = createSelector(
  [(state: RootState) => state.patientDashboard.isLoading],
  isLoading => isLoading,
);

export const patientDocumentAnalysisLoadingStatus = createSelector(
  [(state: RootState) => state.patientDashboard.analyzeReportLoading],
  analyzeReportLoading => analyzeReportLoading,
);
export const selectSpecialistLoadingSelector = createSelector(
  [(state: RootState) => state.patientDashboard.selectSpecialistLoading],
  selectSpecialistLoading => selectSpecialistLoading,
);

export const patientDashboardFailureState = createSelector(
  [(state: RootState) => state.patientDashboard.patientDashboardFailure],
  patientDashboardFailure => patientDashboardFailure,
);
export const patientDashboardSuccessState = createSelector(
  [(state: RootState) => state.patientDashboard.patientDashboardSuccess],
  patientDashboardSuccess => patientDashboardSuccess,
);
export const patientDashboardCurrentStep = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.step],
  currentStep => currentStep,
);
export const patientDashboardSpecialists = createSelector(
  [
    (state: RootState) =>
      state.patientDashboard.dashboardState.recommended_specialists,
  ],
  specialists => specialists.filter(specialist => (specialist?.is_active ?? true)),
);
export const patientDashboardPaymentAmount = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.payment.amount],
  paymentAmount => paymentAmount,
);

export const patientDashboardConsultation = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.consultation_id],
  consultation_id => consultation_id,
);

export const patientDashboardCategory = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.category],
  category => category,
);
export const patientDashboardOriginalCategory = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.original_category],
  category => category,
);

export const patientDashboardSpecialistSelected = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.specialist],
  specialist_selected => specialist_selected,
);

export const patientDashboardHospitalSpecializations = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState?.hospital_specialization ?? []],
  hospital_specializations => hospital_specializations ?? [],
);

export const patientDashboardPaymentUrl = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.payment.url],
  payment_url => payment_url,
);

export const patientDashboardPaymentStatus = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.payment.status],
  payment_status => payment_status,
);

export const patientDashboardPaymentTimerStatus = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.payment.paymentTimer],
  paymentTimer => paymentTimer,
);

export const patientDashboardFiles = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.files],
  files => files,
);

export const patientDashboardPreviousFiles = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.previous_files || []],
  previous_files => previous_files,
);

export const patientDashboardExistingFileIds = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.existing_file_ids || []],
  existing_file_ids => existing_file_ids,
);

export const patientDashboardRejectedFiles = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.rejected_files],
  rejected_files => rejected_files || [],
);

export const patientConsultationsRejectedFiles = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.rejectedFiles],
  rejected_files => rejected_files || [],
);

// Specialist list selectors
export const allSpecialists = createSelector(
  [
    (state: RootState) =>
      state.patientDashboard.specialistList?.specialists || [],
  ],
  specialists => specialists.filter(specialist => (specialist?.is_active ?? true)),
);
export const allSpecialistsCurrentPage = createSelector(
  [
    (state: RootState) =>
      state.patientDashboard.specialistList?.currentPage || 1,
  ],
  currentPage => currentPage,
);

export const allSpecialistsNextPage = createSelector(
  [
    (state: RootState) =>
      state.patientDashboard.specialistList?.nextPage || null,
  ],
  nextPage => nextPage,
);

export const allSpecialistsTotalPages = createSelector(
  [
    (state: RootState) =>
      state.patientDashboard.specialistList?.totalPages || 1,
  ],
  totalPages => totalPages,
);
export const patientDashboardConsultationType = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.consultation_type],
  consultation_type => consultation_type,
);
export const newConsultation = createSelector(
  [(state: RootState) => state.patientDashboard.new_consultation],
  newConsultation => newConsultation,
);

export const consultationList = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.consultation],
  consultation => consultation,
);


export const currentConsultationTab = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.current_tab],
  currentTab => currentTab,
);
export const consultationPage = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.currentPage],
  currentPage => currentPage,
);
export const consultationNextPage = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.nextPage],
  nextPage => nextPage,
);
export const consultationTotalPages = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.totalPages],
  totalPages => totalPages,
);

// Specialist details selectors
export const specialistDetailsLoading = createSelector(
  [(state: RootState) => state.patientDashboard.specialistDetails?.isLoading],
  isLoading => isLoading ?? false,
);

export const specialistDetailsData = createSelector(
  [(state: RootState) => state.patientDashboard.specialistDetails?.data],
  data => data ?? null,
);

// Specialist feedbacks selectors
export const specialistFeedbacksLoading = createSelector(
  [(state: RootState) => state.patientDashboard.specialistFeedbacks?.isLoading],
  isLoading => isLoading ?? false,
);

export const specialistFeedbacksData = createSelector(
  [(state: RootState) => state.patientDashboard.specialistFeedbacks?.feedbacks],
  feedbacks => feedbacks ?? [],
);

// Upload additional files meta selector
export const uploadAdditionalFilesMeta = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.uploadAdditionalFilesMeta],
  uploadAdditionalFilesMeta => uploadAdditionalFilesMeta ?? {},
);

// Expected category selector
export const patientDashboardExpectedCategory = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.expected_category],
  expected_category => expected_category,
);

// Linked case selector
export const patientDashboardLinkedCase = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.linked_to],
  linked_to => linked_to,
);

// Linked case category selector
export const patientDashboardLinkedCaseCategory = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.linked_category],
  linked_category => linked_category,
);

// Manual category selection flag selector
export const patientDashboardManualCategorySelectionFlag = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState?.is_manual_flag ?? false],
  is_manual_flag => is_manual_flag ?? false,
);

// Linked case category selector
export const patientDashboardReportLoading = createSelector(
  [(state: RootState) => state.patientDashboard.uploadFilesLoading],
  uploadFilesLoading => uploadFilesLoading,
);

export const patientDashboardAdditionalFilesLoading = createSelector(
  [(state: RootState) => state.patientDashboard.uploadAdditionalFilesLoading],
  uploadAdditionalFilesLoading => uploadAdditionalFilesLoading,
);

// Cancel consultation loading selector - returns loading state per consultation ID
export const cancelConsultationLoading = createSelector(
  [(state: RootState) => state.patientDashboard.consultations.cancelConsultationLoading],
  cancelConsultationLoading => cancelConsultationLoading ?? {},
);

// Flow selector
export const patientDashboardConsultationFlow = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.flow],
  flow => flow ?? ConsulationFlowType.Regular,
);

// Hospital name selector
export const patientDashboardHospitalName = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.hospital_name],
  hospital_name => hospital_name ?? '',
);

// ==============================================
// Hospital specialists list selectors
// ==============================================
export const hospitalSpecialistsListLoadingStatus = createSelector(
  [(state: RootState) => state.patientDashboard?.hospitalSpecialistsList?.isLoading],
  isLoading => isLoading ?? false,
);
export const hospitalSpecialistsListData = createSelector(
  [(state: RootState) => state.patientDashboard?.hospitalSpecialistsList?.data],
  data => data ?? [],
);

export const patientDashboardPatientQuery = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.patient_query],
  patient_query => patient_query ?? '',
);

export const patientDashboardExpireAt = createSelector(
  [(state: RootState) => state.patientDashboard.dashboardState.expire_at],
  expire_at => expire_at ?? null,
);

// Hospital booking modal persistence
export const hospitalBookingModalState = createSelector(
  [(state: RootState) => state.patientDashboard?.hospitalBookingModalState],
  state => state ?? null,
);

// Recent cases selector
export const recentCasesSelector = createSelector(
  [(state: RootState) => state.patientDashboard?.recentCases],
  recentCases => recentCases?.filter(item => item?.is_active ?? true) ?? [],
);